<template>
  <div class="container h-100">
    <div class="row h-100 justify-content-center align-items-center">
      <div class="col-md-4">
        <h1 class="text-center mb-3">{{ $t('auth.adminLogin.title') }}</h1>

        <Alert :type="alert.type" :message="alert.message"/>

        <!-- Entra ID > Open ID Connector -->
        <div class="my-5">
          <button class="btn btn-primary w-100" :disabled="processing" @click="loginWithMicrosoft">
            <span class="spinner-border spinner-border-sm me-2" v-if="processing"></span>
            <span>{{ $t('auth.adminLogin.authOidc') }}</span>
          </button>
        </div>

        <!-- Email and password login -->
        <div class="mb-3">
          <div
            class="email-login-toggle"
            @click="showEmailForm = !showEmailForm"
            role="button"
            tabindex="0"
            @keypress.enter="showEmailForm = !showEmailForm"
          >
            <span class="email-login-notice">
              {{ $t('auth.adminLogin.emailLoginNotice') }}
            </span>
            <i :class="showEmailForm ? 'bi bi-chevron-up' : 'bi bi-chevron-down'"></i>
          </div>

          <div v-show="showEmailForm" class="email-form-content mt-3">
            <form @submit.prevent="submit">
              <div class="mb-3">
                <input type="email" class="form-control" v-model="email" placeholder="example@town.higashikawa.lg.jp" required/>
              </div>
              <div class="mb-3">
                <div class="password-input-wrapper">
                  <input
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control"
                    v-model="password"
                    :placeholder="$t('auth.adminLogin.password')"
                    required
                  />
                  <button
                    type="button"
                    class="btn-password-toggle"
                    @click="showPassword = !showPassword"
                    tabindex="-1"
                  >
                    <i :class="showPassword ? 'bi bi-eye' : 'bi bi-eye-slash'"></i>
                  </button>
                </div>
              </div>
              <div class="mb-3">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" id="rememberCheckbox" v-model="remember">
                  <label class="form-check-label" for="rememberCheckbox">
                    {{ $t('auth.adminLogin.remember') }}
                  </label>
                </div>
              </div>
              <div class="mb-3">
                <button class="btn btn-primary w-100" :disabled="processing">
                  <span class="spinner-border spinner-border-sm me-2" v-if="processing"></span>
                  <span>{{ $t('auth.adminLogin.submit') }}</span>
                </button>
              </div>
              <div class="mt-4 text-center">
                <router-link :to="{name: 'admin.forgot-password'}" class="forgot-link">{{ $t('auth.adminLogin.forgotPasswordLink') }}</router-link>
              </div>
            </form>
          </div>
        </div>

        <div class="mt-4 text-center">&copy;  {{ $t('common.copyright') }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, onMounted} from 'vue';
import {useRouter} from 'vue-router';
import {useI18n} from 'vue-i18n';
import {loginAdmin} from '../../../api/admin/AuthApi';
import Alert from '../../../components/Alert.vue';

const router = useRouter();
const {t: $t} = useI18n();

const email = ref();
const password = ref();
const remember = ref();
const alert = ref({});
const processing = ref(false);
const showPassword = ref(false);
const showEmailForm = ref(false);

// The callback redirects back here with auth_error=1 when Entra ID sign-in fails.
// Open the email form in that case so there is a way in that does not depend on SSO.
onMounted(() => {
  if (new URLSearchParams(window.location.search).has('auth_error')) {
    alert.value = {type: 'danger', message: $t('auth.adminLogin.error.failed')};
    showEmailForm.value = true;
  }
});

/**
 * Login with Microsoft Entra ID
 */
const loginWithMicrosoft = () => {
  processing.value = true;
  window.location.href = '/auth/microsoft';
}

/**
 * Login
 *
 * @returns {Promise<void>}
 */
const submit = async () => {
  processing.value = true;
  try {
    alert.value = {};
    await loginAdmin(email.value, password.value, remember.value);
    await router.push({name: 'admin.home'});

  } catch (err) {
    alert.value = {
      type: 'danger', message: err.message
    }
  }
  processing.value = false;
}

</script>

<style scoped>
.password-input-wrapper {
  position: relative;
}

.password-input-wrapper .form-control {
  padding-right: 2.5rem;
}

.btn-password-toggle {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-password-toggle:hover {
  color: #495057;
}

.btn-password-toggle:focus {
  outline: none;
}

.btn-password-toggle i {
  font-size: 1.1rem;
}

.email-login-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.email-login-toggle:hover {
  background-color: #e9ecef;
}

.email-login-toggle:focus {
  outline: 2px solid #306FC9;
  outline-offset: 2px;
}

.email-login-notice {
  font-size: 0.9rem;
  font-weight: 500;
}

.email-login-toggle i {
  color: #6c757d;
  font-size: 1rem;
}

.email-form-content {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
